import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import { FileDownload, OpenInNew, Warning } from '@mui/icons-material';

interface FilePreviewModalProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string | null;
  fileName: string | null;
  fileType: 'image' | 'pdf' | null;
  displayName?: string;
  onDownload?: (filename: string, displayName: string) => void;
}

export default function FilePreviewModal({
  open,
  onClose,
  fileUrl,
  fileName,
  fileType,
  displayName,
  onDownload,
}: FilePreviewModalProps) {
  const [iframeError, setIframeError] = useState(false);

  // Reset iframe error state when modal opens/closes
  React.useEffect(() => {
    if (open) {
      setIframeError(false);
    }
  }, [open]);

  if (!fileUrl || !fileName || !fileType) {
    return null;
  }

  const handleDownload = () => {
    if (onDownload && fileName) {
      onDownload(fileName, displayName || fileName);
    }
  };

  const handleOpenInNewTab = () => {
    window.open(fileUrl, '_blank');
  };

  const handleIframeError = () => {
    console.error('Failed to load PDF in iframe:', fileUrl);
    setIframeError(true);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh',
          }
        }
      }}
    >
      <DialogTitle>
        Visualizar: {fileName}
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {fileType === 'image' ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            p: 2,
            minHeight: '60vh'
          }}>
            <img 
              src={fileUrl} 
              alt="Preview" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '60vh', 
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
              onError={(e) => {
                console.error('Failed to load image:', fileUrl);
              }}
            />
          </Box>
        ) : (
          <Box sx={{ 
            width: '100%', 
            height: '70vh',
            minHeight: '500px',
            p: iframeError ? 2 : 0
          }}>
            {iframeError ? (
              // Fallback UI when iframe fails to load
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                gap: 3
              }}>
                <Warning sx={{ fontSize: 64, color: 'warning.main' }} />
                <Typography variant="h6" textAlign="center">
                  Não foi possível visualizar o PDF no navegador
                </Typography>
                <Alert severity="info" sx={{ maxWidth: 500 }}>
                  Alguns navegadores restringem a visualização de PDFs. 
                  Use os botões abaixo para abrir em uma nova aba ou fazer o download.
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={<OpenInNew />}
                    onClick={handleOpenInNewTab}
                    size="large"
                  >
                    Abrir em Nova Aba
                  </Button>
                  {onDownload && (
                    <Button
                      variant="outlined"
                      startIcon={<FileDownload />}
                      onClick={handleDownload}
                      size="large"
                    >
                      Download
                    </Button>
                  )}
                </Box>
              </Box>
            ) : (
              // Try iframe first
              <Box sx={{ 
                width: '100%', 
                height: '100%',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#f5f5f5'
              }}>
                <iframe 
                  src={fileUrl} 
                  width="100%" 
                  height="100%"
                  style={{
                    border: 'none',
                    borderRadius: '8px'
                  }}
                  title={`Preview: ${fileName}`}
                  onError={handleIframeError}
                  onLoad={(e) => {
                    // Check if iframe loaded successfully
                    try {
                      const iframe = e.currentTarget;
                      if (!iframe.contentDocument && !iframe.contentWindow) {
                        handleIframeError();
                      }
                    } catch (error) {
                      handleIframeError();
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
        {fileType === 'pdf' && (
          <Button 
            onClick={handleOpenInNewTab}
            startIcon={<OpenInNew />}
            variant="outlined"
          >
            Abrir em Nova Aba
          </Button>
        )}
        {onDownload && (
          <Button 
            onClick={handleDownload}
            startIcon={<FileDownload />}
            variant="contained"
          >
            Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
} 